<template>
    <!-- Lightweight loader -->
    <div v-if="loading" class="as-loader">
        <div class="spinner-border text-secondary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>

    <div v-else-if="loadError" class="as-page">
        <p class="alert alert-danger" role="alert">{{ loadError }}</p>
    </div>
    <div v-else class="as-page">
        <!-- Logged-in user bar -->
        <div v-if="loggedInName" class="as-user-bar">
            <a :href="dashboardUrl" class="as-dashboard-link">
                <i class="material-icons">dashboard</i>
                Dashboard
            </a>
            <span class="as-user-name">Logged in as {{ loggedInName }}</span>
        </div>

        <!-- Header -->
        <header class="as-header">
            <h1 class="as-title">Volunteer Schedules / Assignments</h1>
            <p v-if="locationName" class="as-subtitle">{{ locationName }}</p>
        </header>

        <!-- Action bar -->
        <div class="as-actions">
            <div class="as-toggles">
                <div class="as-view-toggle" role="group" aria-label="Group events by">
                    <button
                        type="button"
                        class="as-toggle-btn"
                        :class="{ 'as-toggle-btn--active': groupMode === 'activity' }"
                        @click="groupMode = 'activity'"
                    >By activity</button>
                    <button
                        type="button"
                        class="as-toggle-btn"
                        :class="{ 'as-toggle-btn--active': groupMode === 'date' }"
                        @click="groupMode = 'date'"
                    >By date</button>
                </div>
                <div class="as-view-toggle" role="group" aria-label="Report open mode">
                    <button
                        type="button"
                        class="as-toggle-btn"
                        :class="{ 'as-toggle-btn--active': reportMode === 'quick' }"
                        @click="reportMode = 'quick'"
                    >Quick view</button>
                    <button
                        type="button"
                        class="as-toggle-btn"
                        :class="{ 'as-toggle-btn--active': reportMode === 'full' }"
                        @click="reportMode = 'full'"
                    >Report view</button>
                </div>
            </div>
            <div class="as-actions-buttons">
                <button
                    v-if="canGenerateReports"
                    class="as-btn as-btn--outline"
                    @click="generateLocationReports"
                    :disabled="generatingLocationReport"
                >
                    <span v-if="generatingLocationReport" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="material-icons as-btn-icon">description</i>
                    Generate Reports
                </button>
                <button v-if="canViewConsolidated" class="as-btn as-btn--primary" @click="redirectToManageSchedule">
                    <i class="material-icons as-btn-icon">dashboard</i>
                    Consolidated View
                </button>
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="!activities || activities.length === 0" class="as-empty">
            <i class="material-icons as-empty-icon">event_busy</i>
            <p>No upcoming activities to display.</p>
        </div>

        <!-- Activities list (grouped by activity) -->
        <div v-else-if="groupMode === 'activity'" class="as-list">
            <details v-for="activity in activities" :key="activity.activityid" class="as-activity">
                <summary class="as-activity-header">
                    <i class="material-icons as-activity-icon">event</i>
                    <span class="as-activity-name">{{ activity.activityname }}</span>
                    <span class="as-instance-count">{{ instanceCount(activity) }}</span>
                </summary>

                <div class="as-activity-body">
                    <div v-if="activity.instances?.length" class="as-instances">
                        <div v-for="instance in activity.instances" :key="instance.activityinstanceid"
                            class="as-instance">
                            <span class="as-instance-date">{{ instance.formatted_datetime }}</span>
                            <button class="as-icon-btn" title="View Report"
                                :disabled="generatingReports[instance.activityinstanceid]"
                                @click.prevent="openReportModal(instance)">
                                <span v-if="generatingReports[instance.activityinstanceid]"
                                    class="spinner-border spinner-border-sm"></span>
                                <i v-else class="material-icons">visibility</i>
                            </button>
                        </div>
                    </div>
                    <p v-else class="as-no-instances">No upcoming instances for this activity.</p>
                </div>
            </details>
        </div>

        <!-- Events list (grouped by date, ascending) -->
        <div v-else class="as-list">
            <template v-if="dateGroups.length">
                <div v-for="group in dateGroups" :key="group.key" class="as-date-group">
                    <div class="as-date-header">
                        <i class="material-icons as-date-icon">event</i>
                        <span>{{ group.label }}</span>
                        <span class="as-instance-count">{{ group.events.length === 1 ? '1 event' : `${group.events.length} events` }}</span>
                    </div>
                    <div class="as-instances">
                        <div v-for="event in group.events" :key="event.activityinstanceid" class="as-instance">
                            <span class="as-instance-date">
                                <span class="as-event-activity">{{ event.activityname }}</span>
                                <span class="as-event-time">{{ event.time_label }}</span>
                            </span>
                            <button class="as-icon-btn" title="View Report"
                                :disabled="generatingReports[event.activityinstanceid]"
                                @click.prevent="openReportModal(event)">
                                <span v-if="generatingReports[event.activityinstanceid]"
                                    class="spinner-border spinner-border-sm"></span>
                                <i v-else class="material-icons">visibility</i>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <div v-else class="as-empty">
                <i class="material-icons as-empty-icon">event_busy</i>
                <p>No upcoming events to display.</p>
            </div>
        </div>

        <!-- Footer -->
        <footer class="as-footer">
            Powered by <a href="https://churchscheduling.com" target="_blank" rel="noopener noreferrer">churchscheduling.com</a>
        </footer>
    </div>

    <!-- Report Options modal (admin only) -->
    <ContentModal v-model="showOptionsModal" title="Report Options" size="sm">
        <div class="as-options">
            <label class="as-checkbox">
                <input type="checkbox" v-model="optShowDeclined" />
                <span>Include declined assignments</span>
            </label>
            <label class="as-checkbox">
                <input type="checkbox" v-model="optShowStatus" />
                <span>Show assignment status (Confirmed / Pending / Declined)</span>
            </label>
            <label class="as-checkbox">
                <input type="checkbox" v-model="optShowDescription" />
                <span>Show workgroup description</span>
            </label>
            <label class="as-checkbox">
                <input type="checkbox" v-model="optShowResponseMethod" />
                <span>Show response method (how volunteer accepted/declined)</span>
            </label>
            <div class="as-options-actions">
                <button class="as-btn as-btn--outline" @click="showOptionsModal = false">Cancel</button>
                <button class="as-btn as-btn--primary" @click="executeReportGeneration" :disabled="generatingLocationReport || Object.values(generatingReports).some(Boolean)">
                    <span v-if="generatingLocationReport || Object.values(generatingReports).some(Boolean)" class="spinner-border spinner-border-sm me-1"></span>
                    Generate
                </button>
            </div>
        </div>
    </ContentModal>

    <!-- Report modal -->
    <ContentModal v-model="showReportModal" :title="quickView ? 'Report' : 'Report Links'" :size="quickView ? 'xl' : 'md'">
        <div v-if="reportGenerating" class="as-report-progress">
            <p class="as-report-progress-text">Generating report, please wait…</p>
            <div class="as-progress" role="progressbar" aria-label="Generating report">
                <div class="as-progress-bar"></div>
            </div>
        </div>
        <!-- Quick view: HTML report shown inline (no extra clicks) -->
        <template v-else-if="quickView && quickViewUrl">
            <div class="as-quickview">
                <a :href="quickViewUrl" target="_blank" rel="noopener noreferrer" class="as-quickview-open">
                    <i class="material-icons me-1">open_in_new</i> Open in new tab
                </a>
                <iframe :src="quickViewUrl" class="as-quickview-frame" title="Report"></iframe>
            </div>
        </template>
        <template v-else-if="reportResponse">
            <div class="d-flex flex-column gap-3">
                <a v-if="reportResponse.PDF" :href="reportResponse.PDF.url" download
                    class="btn btn-outline-danger w-100 text-decoration-none m-0">
                    <i class="material-icons me-2">picture_as_pdf</i>
                    {{ reportResponse.PDF.label }}
                </a>
                <a v-if="reportResponse.CSV" :href="reportResponse.CSV.url" download
                    class="btn btn-outline-success w-100 text-decoration-none m-0">
                    <i class="material-icons me-2">table_view</i>
                    {{ reportResponse.CSV.label }}
                </a>
                <a v-if="reportResponse.TABLE" :href="reportResponse.TABLE.url" target="_blank"
                    rel="noopener noreferrer" class="btn btn-outline-info w-100 text-decoration-none m-0">
                    <i class="material-icons me-2">visibility</i>
                    {{ reportResponse.TABLE.label }}
                </a>
            </div>
        </template>
    </ContentModal>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ContentModal from '../../components/Modal/ContentModal.vue'
import { getLocationId, getLogginedPersonId } from '../../utils/SessionDataHandler'
import { baseUrl } from '../../utils/BaseUrl'
import { useRoute, useRouter } from 'vue-router'
import { GetToken } from '@/utils/TokenHandler'
import LoggerService from '@/services/LoggerService'

const store = useStore()
const route = useRoute()
const router = useRouter()
// Prefer the URL's locationid on the standalone /all-schedules/:locationid route
// so a logged-in viewer sees the requested location, not their session location.
const isPublicLink = computed(() => route.params.locationid !== undefined)
const scheduleIdentifier = computed(() => isPublicLink.value ? route.params.locationid : getLocationId())
const locationId = ref(null)
const loadError = ref('')
let loadRequestId = 0

const activities = computed(() => store.getters.allSchedules)
const locationMeta = computed(() => store.getters.allSchedulesLocationMeta)
const loading = ref(false)
const showReportModal = ref(false)
const reportResponse = ref(null)
const reportGenerating = ref(false)
const quickView = ref(false)
const quickViewUrl = ref(null)

const generatingReports = ref({})
const cachedReports = ref({})
const currentInstance = ref(null)
const generatingLocationReport = ref(false)
const showOptionsModal = ref(false)
const optShowDeclined = ref(true)
const optShowStatus = ref(true)
const optShowDescription = ref(false)
const optShowResponseMethod = ref(true)
const pendingReportAction = ref(null) // 'location' or instance object

/* --- View grouping (optional date-grouped listing) --- */
const groupMode = ref(route.query.groupby === 'date' ? 'date' : 'activity')

// Keep the URL query in sync so the chosen view is shareable/bookmarkable.
watch(groupMode, (mode) => {
    const query = { ...route.query }
    if (mode === 'date') {
        query.groupby = 'date'
    } else {
        delete query.groupby
    }
    router.replace({ query }).catch(() => {})
})

/* --- Report open mode (quick view opens the HTML report directly) --- */
const reportMode = ref(route.query.report === 'full' ? 'full' : 'quick')

watch(reportMode, (mode) => {
    const query = { ...route.query }
    if (mode === 'full') {
        query.report = 'full'
    } else {
        delete query.report
    }
    router.replace({ query }).catch(() => {})
})

const pad2 = (n) => String(n).padStart(2, '0')

// formatted_datetime is already in the location timezone (e.g. "Feb 22, 2026 6:00:00 PM").
// Parse that so date grouping/labels match what is displayed, regardless of browser TZ.
const parseInstanceDate = (inst) => {
    const raw = inst?.formatted_datetime || inst?.startdtm
    if (!raw) return null
    const d = new Date(raw)
    return isNaN(d.getTime()) ? null : d
}

// Upcoming instances (already limited to the 120-day window by the API), flattened
// across activities, grouped by calendar date and sorted ascending.
const dateGroups = computed(() => {
    const groups = {}
    for (const activity of (activities.value || [])) {
        for (const inst of (activity.instances || [])) {
            if (!inst || inst.activityinstanceid === 'all') continue
            const d = parseInstanceDate(inst)
            if (!d) continue
            const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
            if (!groups[key]) {
                groups[key] = {
                    key,
                    label: d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' }),
                    events: [],
                }
            }
            groups[key].events.push({
                ...inst,
                activityid: activity.activityid,
                activityname: activity.activityname,
                time_label: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
            })
        }
    }
    return Object.values(groups)
        .sort((a, b) => a.key.localeCompare(b.key))
        .map((g) => {
            g.events.sort((a, b) => new Date(a.startdtm) - new Date(b.startdtm))
            return g
        })
})

/* --- Auth & permissions --- */
const isAuthenticated = computed(() => !!GetToken())
const isAdmin = computed(() => !!locationMeta.value?.user?.is_admin_in_location)
const canViewConsolidated = computed(() => {
    return isAuthenticated.value && locationMeta.value?.location?.userscheduleview
})
// Generate Reports button is only available to authenticated admin users
const canGenerateReports = computed(() => isAuthenticated.value && isAdmin.value)
const locationName = computed(() => locationMeta.value?.location?.locationname || '')
const loggedInName = computed(() => {
    const u = locationMeta.value?.user
    if (!u || !isAuthenticated.value) return ''
    return [u.firstname, u.lastname].filter(Boolean).join(' ')
})
const dashboardUrl = computed(() => `${baseUrl}/`)

/* --- Helper --- */
const instanceCount = (activity) => {
    const count = (activity.instances || []).filter(i => i.activityinstanceid !== 'all').length
    return count === 1 ? '1 instance' : `${count} instances`
}

/* --- Access logging --- */
// Records page + report access to the server log via LoggerService. Guests are
// logged as "webuser". Every entry includes the active groupby + report settings.
const logAccess = (action, extra = {}) => {
    const details = {
        user: getLogginedPersonId() ?? 'webuser',
        locationid: locationId.value,
        action,
        groupby: groupMode.value,
        report_mode: reportMode.value,
        ...extra,
    }
    const parts = Object.entries(details).map(
        ([k, v]) => `${k}=${v !== null && typeof v === 'object' ? JSON.stringify(v) : v}`
    )
    LoggerService.logInfo(`AllSchedules access: ${parts.join(', ')}`)
}

/* --- Modal Actions --- */
const openReportModal = (instance) => {
    // Quick view: skip the options popup and take the user straight to the HTML report.
    if (reportMode.value === 'quick') {
        doInstanceQuickView(instance)
        return
    }
    // Report view: existing behavior.
    if (isAdmin.value) {
        // Admin: show options modal first
        pendingReportAction.value = instance
        showOptionsModal.value = true
    } else {
        // Non-admin: generate directly without options (no admin-only fields)
        doInstanceReport(instance, false, false, false, false)
    }
}

/* --- Report readiness (report/links open before the file is ready) --- */
const REPORT_READY_TIMEOUT_MS = 15000
const REPORT_POLL_INTERVAL_MS = 500

// Quick view opens the HTML (TABLE) report; fall back to PDF only if missing.
const quickViewReportUrl = (report) => report?.TABLE?.url || report?.PDF?.url || null
const primaryReportUrl = (report) => report?.TABLE?.url || report?.PDF?.url || report?.CSV?.url || null

// Poll the generated report URL until it is reachable, up to a max timeout.
// Returns true once reachable, false if it times out (CORS/opaque/slow generation).
const waitForReportReady = async (report) => {
    const url = primaryReportUrl(report)
    if (!url) {
        await new Promise((resolve) => setTimeout(resolve, REPORT_READY_TIMEOUT_MS))
        return false
    }
    const deadline = Date.now() + REPORT_READY_TIMEOUT_MS
    while (Date.now() < deadline) {
        try {
            const res = await fetch(url, { method: 'HEAD', cache: 'no-store' })
            if (res && res.ok) return true
        } catch (e) {
            // Network/CORS error — keep waiting until the deadline, then reveal.
        }
        await new Promise((resolve) => setTimeout(resolve, REPORT_POLL_INTERVAL_MS))
    }
    console.warn('Report readiness check timed out; revealing anyway.')
    return false
}

// Quick view: generate the instance report, wait for it to be reachable (progress
// meter), then show the HTML report inline — no options popup, no link list.
const doInstanceQuickView = async (instance) => {
    const requestId = loadRequestId
    currentInstance.value = instance
    generatingReports.value[instance.activityinstanceid] = true
    logAccess('instance_view', {
        activityinstanceid: instance.activityinstanceid,
        quick_view: true,
        report_options: { showDeclinedRecords: false, showAssignmentStatus: false, showWorkgroupDescription: false, showResponseMethod: true },
    })
    reportResponse.value = null
    quickViewUrl.value = null
    quickView.value = true
    reportGenerating.value = true
    showReportModal.value = true
    try {
        const payload = {
            ...(instance.pdf_params || {
                cmd: 'getpdf',
                instanceid: instance.activityinstanceid
            }),
            locationid: scheduleIdentifier.value,
            public_link: isPublicLink.value ? 1 : 0,
            showDeclinedRecords: false,
            showAssignmentStatus: false,
            showWorkgroupDescription: false,
            showResponseMethod: true,
        }
        const report = await store.dispatch('generateReport', payload)
        if (requestId !== loadRequestId) return
        if (report) {
            cachedReports.value[instance.activityinstanceid] = report
            await waitForReportReady(report)
            if (requestId !== loadRequestId) return
            reportResponse.value = report
            quickViewUrl.value = quickViewReportUrl(report)
        } else {
            showReportModal.value = false
        }
    } catch (err) {
        if (requestId !== loadRequestId) return
        console.error('Failed to generate report', err)
        showReportModal.value = false
    } finally {
        if (requestId === loadRequestId) {
            generatingReports.value[instance.activityinstanceid] = false
            reportGenerating.value = false
        }
    }
}

const doInstanceReport = async (instance, showDeclined, showStatus, showDescription = false, showResponseMethod = true) => {
    const requestId = loadRequestId
    currentInstance.value = instance
    generatingReports.value[instance.activityinstanceid] = true
    logAccess('instance_view', {
        activityinstanceid: instance.activityinstanceid,
        quick_view: false,
        report_options: { showDeclinedRecords: showDeclined, showAssignmentStatus: showStatus, showWorkgroupDescription: showDescription, showResponseMethod },
    })
    // Open the modal immediately in a "generating" state so the user sees progress.
    reportResponse.value = null
    quickView.value = false
    reportGenerating.value = true
    showReportModal.value = true
    try {
        const payload = {
            ...(instance.pdf_params || {
                cmd: 'getpdf',
                instanceid: instance.activityinstanceid
            }),
            locationid: scheduleIdentifier.value,
            public_link: isPublicLink.value ? 1 : 0,
            showDeclinedRecords: showDeclined,
            showAssignmentStatus: showStatus,
            showWorkgroupDescription: showDescription,
            showResponseMethod: showResponseMethod,
        }
        const report = await store.dispatch('generateReport', payload)
        if (requestId !== loadRequestId) return
        if (report) {
            cachedReports.value[instance.activityinstanceid] = report
            // Wait until the generated file is actually reachable before revealing links.
            await waitForReportReady(report)
            if (requestId !== loadRequestId) return
            reportResponse.value = report
        } else {
            showReportModal.value = false
        }
    } catch (err) {
        if (requestId !== loadRequestId) return
        console.error('Failed to generate report', err)
        showReportModal.value = false
    } finally {
        if (requestId === loadRequestId) {
            generatingReports.value[instance.activityinstanceid] = false
            reportGenerating.value = false
        }
    }
}

/* --- Location-level Report --- */
const generateLocationReports = () => {
    if (isAdmin.value) {
        pendingReportAction.value = 'location'
        showOptionsModal.value = true
    } else {
        doLocationReport(false, false, false, false)
    }
}

const doLocationReport = async (showDeclined, showStatus, showDescription = false, showResponseMethod = true) => {
    const requestId = loadRequestId
    generatingLocationReport.value = true
    logAccess('location_report', {
        quick_view: false,
        report_options: { showDeclinedRecords: showDeclined, showAssignmentStatus: showStatus, showWorkgroupDescription: showDescription, showResponseMethod },
    })
    // Open the modal immediately in a "generating" state so the user sees progress.
    reportResponse.value = null
    quickView.value = false
    reportGenerating.value = true
    showReportModal.value = true
    try {
        const allInstanceIds = []
        const allWorkgroupIds = []
        if (activities.value) {
            for (const activity of activities.value) {
                if (activity.instances) {
                    for (const inst of activity.instances) {
                        if (inst.activityinstanceid && inst.activityinstanceid !== 'all') {
                            allInstanceIds.push(String(inst.activityinstanceid))
                        }
                        if (inst.workgroup_ids) {
                            allWorkgroupIds.push(...inst.workgroup_ids)
                        }
                    }
                }
            }
        }
        const report = await store.dispatch('generateLocationReport', {
            locationid: locationId.value,
            public_bool: isAuthenticated.value ? '0' : '1',
            activityidsarray: [],
            activityinstanceidsarray: allInstanceIds,
            instanceworkgroupidsarray: allWorkgroupIds,
            reporttype: 'list',
            showDeclinedRecords: showDeclined,
            showAssignmentStatus: showStatus,
            showWorkgroupDescription: showDescription,
            showResponseMethod: showResponseMethod,
        })
        if (requestId !== loadRequestId) return
        if (report) {
            // Wait until the generated file is actually reachable before revealing links.
            await waitForReportReady(report)
            if (requestId !== loadRequestId) return
            reportResponse.value = report
        } else {
            showReportModal.value = false
        }
    } catch (err) {
        if (requestId !== loadRequestId) return
        console.error('Failed to generate location report', err)
        showReportModal.value = false
    } finally {
        if (requestId === loadRequestId) {
            generatingLocationReport.value = false
            reportGenerating.value = false
        }
    }
}

/* --- Execute from options modal --- */
const executeReportGeneration = () => {
    showOptionsModal.value = false
    const declined = optShowDeclined.value
    const status = optShowStatus.value
    const description = optShowDescription.value
    const responseMethod = optShowResponseMethod.value
    if (pendingReportAction.value === 'location') {
        doLocationReport(declined, status, description, responseMethod)
    } else if (pendingReportAction.value) {
        doInstanceReport(pendingReportAction.value, declined, status, description, responseMethod)
    }
}

/* --- Navigation --- */
const redirectToManageSchedule = () => {
    const url = `${baseUrl}/MF/cs-mf-manageschedules/`
    try {
        const targetWindow = window.top || window
        targetWindow.location.href = url
    } catch (e) {
        window.location.href = url
    }
}

/* --- Init --- */
watch(scheduleIdentifier, async (identifier) => {
    const requestId = ++loadRequestId
    loading.value = true
    loadError.value = ''
    locationId.value = null
    showReportModal.value = false
    showOptionsModal.value = false
    reportResponse.value = null
    quickViewUrl.value = null
    generatingReports.value = {}
    generatingLocationReport.value = false
    reportGenerating.value = false
    cachedReports.value = {}
    pendingReportAction.value = null
    try {
        const data = await store.dispatch('getAllSchedules', {
            locationid: identifier,
            publicLink: isPublicLink.value,
        })
        if (requestId !== loadRequestId || !data) return
        locationId.value = Number(data.location.locationid)
        logAccess('page_view')
    } catch (error) {
        if (requestId === loadRequestId) {
            loadError.value = 'Unable to load schedules. This link may be invalid or unavailable. Please request a new link from the location administrator.'
        }
    } finally {
        if (requestId === loadRequestId) loading.value = false
    }
}, { immediate: true })

onBeforeUnmount(() => {
    loadRequestId++
    store.dispatch('clearAllSchedules')
})
</script>

<style scoped>
/* ── Page reset (embed-friendly) ── */
.as-page {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, sans-serif;
    max-width: 800px;
    margin: 0 auto;
    padding: 24px 16px;
    color: #2c3e50;
}

/* ── Loader ── */
.as-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
}

/* ── Header ── */
.as-header {
    text-align: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e9ecef;
}

.as-title {
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0;
    color: #344767;
    letter-spacing: -0.01em;
}

.as-subtitle {
    font-size: 0.9rem;
    color: #7b809a;
    margin: 6px 0 0;
}

/* ── User bar ── */
.as-user-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 8px 12px;
    background: #f8f9fa;
    border-radius: 6px;
    font-size: 0.82rem;
}

.as-dashboard-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #4a90d9;
    text-decoration: none;
    font-weight: 500;
}

.as-dashboard-link:hover {
    text-decoration: underline;
}

.as-dashboard-link .material-icons {
    font-size: 16px;
}

.as-user-name {
    color: #7b809a;
}

/* ── Action bar ── */
.as-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.as-actions-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

/* ── View toggle ── */
.as-view-toggle {
    display: inline-flex;
    border: 1px solid #ced4da;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
}

.as-toggle-btn {
    padding: 6px 14px;
    font-size: 0.8rem;
    font-weight: 500;
    border: none;
    background: #fff;
    color: #495057;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}

.as-toggle-btn + .as-toggle-btn {
    border-left: 1px solid #ced4da;
}

.as-toggle-btn--active {
    background: #4a90d9;
    color: #fff;
}

.as-toggles {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

/* ── Quick view (inline HTML report) ── */
.as-quickview {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.as-quickview-open {
    align-self: flex-end;
    display: inline-flex;
    align-items: center;
    font-size: 0.8rem;
    color: #4a90d9;
    text-decoration: none;
}

.as-quickview-open:hover {
    text-decoration: underline;
}

.as-quickview-frame {
    width: 100%;
    height: 65vh;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #fff;
}

.as-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    font-size: 0.82rem;
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
}

.as-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.as-btn-icon {
    font-size: 18px;
}

.as-btn--outline {
    background: #fff;
    color: #495057;
    border-color: #ced4da;
}

.as-btn--outline:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #adb5bd;
}

.as-btn--primary {
    background: #4a90d9;
    color: #fff;
    border-color: #4a90d9;
}

.as-btn--primary:hover:not(:disabled) {
    background: #3b7dd8;
}

/* ── Empty state ── */
.as-empty {
    text-align: center;
    padding: 48px 16px;
    color: #999;
}

.as-empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.4;
}

/* ── Activity list ── */
.as-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.as-activity {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.as-activity[open] {
    border-color: #d0d7de;
}

.as-activity-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    cursor: pointer;
    user-select: none;
    font-size: 0.95rem;
    font-weight: 500;
    color: #344767;
    list-style: none;
}

.as-activity-header::-webkit-details-marker {
    display: none;
}

.as-activity-header:hover {
    background: #f8f9fa;
}

.as-activity[open] > .as-activity-header {
    background: #f0f6ff;
    border-bottom: 1px solid #e0e8f0;
}

.as-activity-icon {
    font-size: 20px;
    color: #4a90d9;
}

.as-activity-name {
    flex: 1;
}

.as-instance-count {
    font-size: 0.75rem;
    color: #7b809a;
    font-weight: 400;
    background: #f1f3f5;
    padding: 2px 10px;
    border-radius: 12px;
}

/* ── Instances ── */
.as-activity-body {
    padding: 8px 0;
}

.as-instances {
    display: flex;
    flex-direction: column;
}

.as-instance {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px 8px 48px;
    font-size: 0.87rem;
    color: #495057;
    transition: background 0.1s;
}

.as-instance:hover {
    background: #fafbfc;
}

.as-instance-date {
    flex: 1;
}

.as-icon-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #4a90d9;
    border-radius: 4px;
    transition: background 0.1s;
    display: flex;
    align-items: center;
}

.as-icon-btn:hover:not(:disabled) {
    background: #e7f3ff;
}

.as-icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.as-icon-btn .material-icons {
    font-size: 20px;
}

.as-no-instances {
    padding: 12px 48px;
    color: #999;
    font-size: 0.85rem;
}

/* ── Date groups ── */
.as-date-group {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
}

.as-date-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    font-size: 0.95rem;
    font-weight: 600;
    color: #344767;
    background: #f0f6ff;
    border-bottom: 1px solid #e0e8f0;
}

.as-date-icon {
    font-size: 20px;
    color: #4a90d9;
}

.as-date-header > span:first-of-type {
    flex: 1;
}

.as-event-activity {
    font-weight: 500;
    color: #344767;
}

.as-event-time {
    margin-left: 8px;
    color: #7b809a;
    font-size: 0.82rem;
}

/* ── Report progress ── */
.as-report-progress {
    padding: 12px 0;
    text-align: center;
}

.as-report-progress-text {
    font-size: 0.9rem;
    color: #495057;
    margin-bottom: 12px;
}

.as-progress {
    position: relative;
    height: 8px;
    width: 100%;
    background: #e9ecef;
    border-radius: 999px;
    overflow: hidden;
}

.as-progress-bar {
    position: absolute;
    top: 0;
    left: -40%;
    height: 100%;
    width: 40%;
    border-radius: 999px;
    background: #4a90d9;
    animation: as-progress-indeterminate 1.2s ease-in-out infinite;
}

@keyframes as-progress-indeterminate {
    0% { left: -40%; }
    100% { left: 100%; }
}

/* ── Footer ── */
.as-footer {
    text-align: center;
    margin-top: 32px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
    font-size: 0.78rem;
    color: #adb5bd;
}

.as-footer a {
    color: #7b809a;
    text-decoration: none;
}

.as-footer a:hover {
    text-decoration: underline;
    color: #4a90d9;
}

/* ── Options modal ── */
.as-options {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 8px 0;
}

.as-checkbox {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 0.88rem;
    color: #344767;
}

.as-checkbox input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #4a90d9;
    cursor: pointer;
    flex-shrink: 0;
}

.as-options-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
}
</style>
